import { useState, useEffect, useRef } from 'react';
import { Search, Loader2, X } from 'lucide-react';
import { Input } from './ui/input';
import { Card } from './ui/card';
import { Product } from '@/types';
import { useNavigate } from 'react-router-dom';
import { API_CONFIG } from '@/config/api';

interface SearchBoxProps {
  className?: string;
  placeholder?: string;
  onSearch?: (query: string) => void; // { changed code }
}

interface SearchHit {
  _source: {
    name?: string;
    product_number?: string;
    groupby_key?: string;
    brand_label?: string;
    id?: string;
    sku?: string;
    price?: number;
    f_price?: number;
  };
}

interface SearchResponse {
  hits?: {
    hits?: SearchHit[];
  };
}

interface AutoCompleteOption {
  label: string;
  type: 'suggestion' | 'category' | 'brand';
  value: string;
  id?: string;
}

interface AutoCompleteGroup {
  label: string;
  options: AutoCompleteOption[];
}

export const SearchBox = ({ className, placeholder = 'Search for "Bearing"',onSearch }: SearchBoxProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [autoComplete, setAutoComplete] = useState<AutoCompleteGroup[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState<number>(-1);
  const searchRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
        setHoveredIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch suggestions when search query changes
  useEffect(() => {
    const fetchAutoComplete = async () => {
      if (searchQuery.trim().length < 2) {
        setAutoComplete([]);
        setShowSuggestions(false);
        return;
      }

      setIsLoading(true);
      try {
        const params = new URLSearchParams();
        params.append('search', searchQuery.trim());
const q =     {"q":{"_source": [
                          "groupby_key",
                          "brand_label"
                      ],
                      "query": {
                        "bool": {
                          "must": [
                                      {
                                          "multi_match": {
                                              "query": searchQuery.trim(),
                                              "fields": [
                                                "groupby_key^1000",
                                                  "search_keywords^500",
                                                  "sku^9",
                                                  "category_names^200",
                                                  "brand_label^500"
                                              ],
                                              "type":"best_fields",
                                              "operator":"and",
                                              "fuzziness":1,
                                          }
                                      },
                                      {
                                          "bool":  {
                                           "filter": [
                                                  {
                                                    "term": {
                                                      "product_type": 2
                                                    }
                                                  }
                                                ]
                                      }
                                      }
                                     
                                  ]
                        }
                      }
                  }};
        const url = `${API_CONFIG.DEFAULT_IMAGE_BASE_URL}/api/productSearch`;

        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(q),
        });

        if (!response.ok) {
          throw new Error(`API error: ${response.status}`);
        }

        // robust JSON parse (handles text responses too)
        let resMulti: any;
        try {
          resMulti = await response.json();
        } catch (err) {
          // fallback: try text -> parse
          const txt = await response.text();
          try {
            resMulti = JSON.parse(txt);
          } catch (err2) {
            console.error('Failed to parse autocomplete response', txt);
            throw new Error('Invalid response from autocomplete API');
          }
        }

        console.log('Autocomplete raw response:', resMulti);

        // Handle multi-part response safely
        const res = Array.isArray(resMulti) ? resMulti[0] : resMulti;
        const res_cat = Array.isArray(resMulti) && resMulti.length > 1 ? resMulti[1] : null;

        // Handle multi-call response (products, categories, brands)
        const searchOptions: AutoCompleteGroup[] = [
          {
            label: 'Suggestions',
            options: [],
          },
          // {
          //   label: 'Categories',
          //   options: [],
          // },
          // {
          //   label: 'Brands',
          //   options: [],
          // },
        ];

        const array_strng_GroupBy: string[] = [];
        const array_strng_Brands: string[] = [];

        // Process suggestions (products)
        console.log('Autocomplete response:', res,resMulti);
        if (res?.hits?.hits && res.hits.hits.length > 0) {
          const suggestionOptions: AutoCompleteOption[] = [];

          res.hits.hits.forEach((element: SearchHit) => {
            const name =
              element._source.name === null
                ? element._source.product_number
                : element._source.name;

            if (element._source.groupby_key && element._source.groupby_key !== '') {
              if (!array_strng_GroupBy.includes(element._source.groupby_key)) {
                array_strng_GroupBy.push(element._source.groupby_key);
                suggestionOptions.push({
                  label: element._source.groupby_key,
                  type: 'suggestion',
                  value: element._source.groupby_key,
                  id: element._source.id || element._source.sku,
                });
              }
            } else if (name) {
              suggestionOptions.push({
                label: name,
                type: 'suggestion',
                value: name,
                id: element._source.id || element._source.sku,
              });
            }

            if (element._source.brand_label && element._source.brand_label !== '') {
              if (!array_strng_Brands.includes(element._source.brand_label)) {
                array_strng_Brands.push(element._source.brand_label);
              }
            }
          });

          searchOptions[0].options = suggestionOptions;
        }

        // Process categories
        // if (res_cat?.hits?.hits && res_cat.hits.hits.length > 0) {
        //   const categoryOptions: AutoCompleteOption[] = [];

        //   res_cat.hits.hits.forEach((element: SearchHit) => {
        //     const name = element._source.name;
        //     const id = element._source.id;

        //     if (name) {
        //       categoryOptions.push({
        //         label: name,
        //         type: 'category',
        //         value: name,
        //         id: `Cat_${id}`,
        //       });
        //     }
        //   });

        //   searchOptions[1].options = categoryOptions;
        // }

        // Process brands
        const brandOptions: AutoCompleteOption[] = [];
        // array_strng_Brands.forEach((brand) => {
        //   if (brand.toLowerCase().includes(searchQuery.trim().toLowerCase())) {
        //     brandOptions.push({
        //       label: brand,
        //       type: 'brand',
        //       value: brand,
        //     });
        //   }
        // });

        if (brandOptions.length > 0) {
          searchOptions[2].options = brandOptions;
        }

        // Only show if we have results
        const hasResults =
          searchOptions[0].options.length > 0 ||
          searchOptions[1].options.length > 0 ||
          searchOptions[2].options.length > 0;

        if (hasResults) {
          setAutoComplete(searchOptions);
          setShowSuggestions(true);
        } else {
          setAutoComplete([]);
          setShowSuggestions(true);
        }
      } catch (error) {
        console.error('Error fetching autocomplete:', error);
        setAutoComplete([]);
      } finally {
        setIsLoading(false);
      }
    };

    // Debounce search
    const debounceTimer = setTimeout(() => {
      fetchAutoComplete();
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [searchQuery]);

  const handleOptionClick = (option: AutoCompleteOption) => {
    if (option.type === 'suggestion') {
      console.log('Navigating to product:', option);
      setSearchQuery(option.label);
       onSearch(option.value);

      
      // navigate(`/product/${option.id || option.value}`);
    } else if (option.type === 'category') {
      navigate(`/category/${option.id}`);
    } else if (option.type === 'brand') {
      navigate(`/search?brand=${encodeURIComponent(option.value)}`);
    }
    // setSearchQuery('');
    setAutoComplete([]);
    setShowSuggestions(false);
    setHoveredIndex(-1);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setAutoComplete([]);
    setShowSuggestions(false);
    setHoveredIndex(-1);
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
      setShowSuggestions(false);
      setHoveredIndex(-1);
    }
  };

  const flattenedOptions = autoComplete.flatMap((group) => group.options);
  let currentIndex = 0;

  return (
    <div ref={searchRef} className={`relative w-full flex items-center justify-center ${className}`}>
      <div className="relative w-full md:w-4/5">
        <form onSubmit={handleSearchSubmit}>
          <div className="relative w-full">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
            <Input
              type="text"
              placeholder={placeholder}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => {
                if (autoComplete.length > 0) {
                  setShowSuggestions(true);
                }
              }}
              className="pl-12 pr-12 w-full h-12 text-base"
            />
            {isLoading && (
              <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground animate-spin" />
            )}
            {!isLoading && searchQuery && (
              <button
                type="button"
                onClick={handleClearSearch}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="h-5 w-5" />
              </button>
            )}
          </div>
        </form>

        {/* Autocomplete Dropdown */}
        {showSuggestions && flattenedOptions.length > 0 && (
          <Card className="absolute top-full left-0 right-0 mt-2 max-h-96 overflow-y-auto z-[100] shadow-2xl border-2 border-border bg-background">
            <div className="p-2 space-y-2">
              {autoComplete.map((group) => {
                if (group.options.length === 0) return null;

                return (
                  <div key={group.label}>
                    <div className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                      {group.label}
                    </div>
                    <div className="space-y-1">
                      {group.options.map((option) => {
                        const isHovered = hoveredIndex === currentIndex;
                        const result = (
                          <button
                            key={`${group.label}-${option.value}`}
                            onClick={() => handleOptionClick(option)}
                            onMouseEnter={() => setHoveredIndex(currentIndex)}
                            onMouseLeave={() => setHoveredIndex(-1)}
                            className={`w-full flex items-center gap-3 px-3 py-2 rounded-md transition-all duration-150 text-left ${
                              isHovered
                                ? 'bg-primary/15 border border-primary/40 shadow-sm'
                                : 'bg-transparent border border-transparent hover:bg-accent/50'
                            }`}
                          >
                            {option.type === 'suggestion' && (
                              <Search className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                            )}
                            {option.type === 'category' && (
                              <div className="h-4 w-4 rounded bg-blue-200 flex-shrink-0" />
                            )}
                            {option.type === 'brand' && (
                              <div className="h-4 w-4 rounded bg-purple-200 flex-shrink-0" />
                            )}

                            <div className="flex-1 min-w-0">
                              <p
                                className={`text-sm line-clamp-1 transition-colors ${
                                  isHovered ? 'text-primary font-medium' : 'text-foreground'
                                }`}
                              >
                                {option.label}
                              </p>
                            </div>
                          </button>
                        );
                        currentIndex++;
                        return result;
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        )}

        {/* No Results */}
        {showSuggestions && !isLoading && searchQuery.length >= 2 && flattenedOptions.length === 0 && (
          <Card className="absolute top-full left-0 right-0 mt-2 z-50 shadow-lg">
            <div className="p-6 text-center">
              <Search className="h-12 w-12 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No results found for "{searchQuery}"</p>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};
